import {
  GenericForm,
  GenericFormProps,
} from "@/lib/Components/Form/Forms/GenericForm";
import { ReactNode } from "react";
import { FormikValues } from "formik";
import { Button } from "@/components/catalyst/button";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { toast } from "sonner";
import { Badge } from "@/components/catalyst/badge";
import { cn } from "@/lib/utils";

export function SettingFormCard<T extends FormikValues>({
  title,
  description,
  children,
  onSubmit,
  saveButtonPosition = "bottom",
  ...props
}: {
  title: ReactNode;
  description: ReactNode;
  saveButtonPosition?: "bottom" | "top";
} & GenericFormProps<T>) {
  return (
    <GenericForm
      className="block space-y-6 rounded-md bg-white p-6 shadow-sm"
      enableReinitialize
      initialEditingState={true}
      onSubmit={async (...props) => {
        const res = await onSubmit?.(...props);

        toast.success("Settings saved successfully");

        return res;
      }}
      {...props}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 font-semibold leading-7 text-gray-900">
            {title} <UnsavedChanged />
          </h3>
          <p className="mt-1 text-sm text-gray-500">{description}</p>
        </div>
        {saveButtonPosition === "top" ? <SaveButton inline /> : null}
      </div>
      <div className="grid grid-cols-3 gap-6 sm:grid-cols-12">{children}</div>
      {saveButtonPosition === "bottom" ? <SaveButton /> : null}
    </GenericForm>
  );
}

function UnsavedChanged() {
  const { hasUnsavedChanges } = useFormState();

  if (!hasUnsavedChanges) {
    return null;
  }

  return <Badge color="amber">Unsaved changes</Badge>;
}

export function SettingCard({
  title,
  description,
  children,
  buttons,
}: {
  title: ReactNode;
  description: ReactNode;
  children: ReactNode;
  buttons?: ReactNode;
}) {
  return (
    <div className="relative space-y-6 rounded-md bg-white p-6 shadow-sm">
      <div>
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {title}
        </h3>

        <p className="mt-1 text-sm text-gray-500">{description}</p>
      </div>
      <div className="">{children}</div>

      <div className="absolute right-6 top-6">{buttons}</div>
    </div>
  );
}

function SaveButton({ inline }: { inline?: boolean }) {
  const { isSubmitting, submitCount, isValid, hasUnsavedChanges } =
    useFormState();
  const showWarning = submitCount > 1 && !isValid;

  if (inline) {
    return (
      <div
        className={cn(
          "flex shrink-0 gap-2 transition-opacity",
          hasUnsavedChanges ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      >
        <Button plain type="reset">
          Reset
        </Button>
        <Button
          color={showWarning ? "red" : "indigo"}
          LeadingIcon={showWarning ? ExclamationTriangleIcon : undefined}
          showCommandShortcut
          isLoading={isSubmitting}
          type="submit"
        >
          Save
        </Button>
      </div>
    );
  }

  if (!hasUnsavedChanges) {
    return null;
  }

  return (
    <div className="flex justify-end space-x-3 border-t border-gray-100 pt-6">
      <Button plain type="reset">
        Reset
      </Button>
      <Button
        color={showWarning ? "red" : "indigo"}
        LeadingIcon={showWarning ? ExclamationTriangleIcon : undefined}
        showCommandShortcut
        isLoading={isSubmitting}
        type="submit"
      >
        Submit
      </Button>
    </div>
  );
}
